import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '@shared/ui/Button';

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  /** With total (+ pageSize), renders "Showing 1–20 of 635 <noun>" on the left. */
  total?: number;
  pageSize?: number;
  noun?: string;
  className?: string;
}

/**
 * The pager, in one place.
 *
 * Six surfaces hand-rolled Previous/Next + "Page X of Y" with drifting
 * borders, icon-only vs labeled buttons, and their own disabled logic;
 * admin-next carried its own ListPager (now a thin wrapper over this).
 *
 * Renders nothing when there is at most one page. Labeled Previous/Next
 * (not bare chevrons) — icon-only pagers were unlabeled for screen readers.
 */
const Pagination: React.FC<PaginationProps> = ({
  page,
  pageCount,
  onPageChange,
  total,
  pageSize,
  noun,
  className = '',
}) => {
  if (pageCount <= 1) return null;

  const showRange = total !== undefined && pageSize !== undefined;
  const rangeStart = showRange ? (page - 1) * (pageSize as number) + 1 : 0;
  const rangeEnd = showRange ? Math.min(page * (pageSize as number), total as number) : 0;

  return (
    <div
      className={`flex items-center ${showRange ? 'justify-between' : 'justify-center'} text-sm text-text-secondary dark:text-text-dark-secondary ${className}`}
    >
      {showRange && (
        <span>
          Showing {rangeStart}–{rangeEnd} of {total}
          {noun ? ` ${noun}` : ''}
        </span>
      )}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          icon={ChevronLeft}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </Button>
        <span>
          Page {page} of {pageCount}
        </span>
        <Button
          variant="outline"
          size="sm"
          icon={ChevronRight}
          iconPosition="right"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
